import { AnimationFactory } from "../types";

type Particle = {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number; // 1 -> 0
    size: number;
    rot: number;
};

type Lightning = {
    points: { x: number; y: number }[];
    life: number; // 1 -> 0
    width: number;
};

const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

const easeOutCubic = (t: number) => {
    t = clamp01(t);
    return 1 - Math.pow(1 - t, 3);
};

const easeInOutQuad = (t: number) => {
    t = clamp01(t);
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
};

const makeRng = (seed: number) => {
    let s = seed >>> 0;
    return () => {
        s ^= s << 13;
        s ^= s >>> 17;
        s ^= s << 5;
        return (s >>> 0) / 4294967296;
    };
};

const TAU = Math.PI * 2;

export const createMassDestroyAnimation: AnimationFactory = ({
                                                                 ctx,
                                                                 canvas,
                                                                 startX,
                                                                 startY,
                                                                 endX,
                                                                 endY,
                                                                 startTime,
                                                                 scale,
                                                             }) => {
    const W = canvas.width;
    const H = canvas.height;

    const cx = endX;
    const cy = endY - canvas.height * 0.1;

    const s = scale ?? 1;

    const maxR = 0.66 * Math.min(W, H);
    const coreMax = Math.min(W, H) * 0.14 * s;

    const FLIGHT_MS = 420;
    const BURST_MS = 980;
    const TOTAL_MS = FLIGHT_MS + BURST_MS;

    const rng = makeRng(
        (startTime | 0) ^ ((startX * 73856093) | 0) ^ ((startY * 19349663) | 0)
    );

    const particles: Particle[] = [];
    const lightnings: Lightning[] = [];
    let spawned = false;

    const spawnParticles = () => {
        particles.length = 0;
        const count = 160;

        for (let i = 0; i < count; i++) {
            const a = rng() * TAU;
            const spd = lerp(140, 860, rng()) * s;

            particles.push({
                x: cx,
                y: cy,
                vx: Math.cos(a) * spd,
                vy: Math.sin(a) * spd,
                life: lerp(0.7, 1.0, rng()),
                size: lerp(1.0, 6.0, rng()) * s,
                rot: rng() * TAU,
            });
        }
    };

    const spawnLightning = (count: number) => {
        for (let i = 0; i < count; i++) {
            const a = rng() * TAU;
            const len = lerp(maxR * 0.35, maxR * 0.95, rng());
            const steps = 10 + Math.floor(rng() * 8);

            const pts: { x: number; y: number }[] = [];
            pts.push({ x: cx, y: cy });

            let px = cx;
            let py = cy;

            for (let k = 1; k <= steps; k++) {
                const t = k / steps;
                const baseX = cx + Math.cos(a) * len * t;
                const baseY = cy + Math.sin(a) * len * t;

                // відхилення “зламаності”
                const jitter = (1 - t) * 18 * s + 6 * s;
                const jx = (rng() - 0.5) * jitter * 2;
                const jy = (rng() - 0.5) * jitter * 2;

                px = baseX + jx;
                py = baseY + jy;
                pts.push({ x: px, y: py });

                // інколи робимо маленьку гілку
                if (k > 3 && k < steps - 2 && rng() < 0.18) {
                    const ba = a + (rng() - 0.5) * 1.2;
                    const bl = len * lerp(0.15, 0.35, rng());
                    const bx = px + Math.cos(ba) * bl;
                    const by = py + Math.sin(ba) * bl;
                    pts.push({ x: bx, y: by });
                    pts.push({ x: px, y: py });
                }
            }

            lightnings.push({
                points: pts,
                life: 1,
                width: lerp(1.5, 3.5, rng()) * s,
            });
        }
    };

    const updateParticles = (dt: number) => {
        // dt in seconds
        for (const p of particles) {
            p.life -= dt * 0.75;

            // drag
            p.vx *= 1 - dt * 0.9;
            p.vy *= 1 - dt * 0.9;

            // вакуум назад у центр
            const dx = cx - p.x;
            const dy = cy - p.y;
            const d = Math.hypot(dx, dy) || 1;
            const pull = 22 * s;

            p.vx += (dx / d) * pull * dt;
            p.vy += (dy / d) * pull * dt;

            p.x += p.vx * dt;
            p.y += p.vy * dt;

            p.rot += dt * 6;
        }

        for (let i = particles.length - 1; i >= 0; i--) {
            if (particles[i].life <= 0) particles.splice(i, 1);
        }
    };

    const updateLightning = (dt: number) => {
        for (const l of lightnings) l.life -= dt * 1.6;
        for (let i = lightnings.length - 1; i >= 0; i--) {
            if (lightnings[i].life <= 0) lightnings.splice(i, 1);
        }
    };

    const drawBolt = (x: number, y: number, t01: number) => {
        const dx = cx - startX;
        const dy = cy - startY;
        const len = Math.hypot(dx, dy) || 1;
        const nx = dx / len;
        const ny = dy / len;

        const tailLen = lerp(55, 150, t01) * s;

        ctx.save();

        // чорний шлейф
        ctx.globalCompositeOperation = "source-over";
        ctx.strokeStyle = "rgba(0,0,0,0.65)";
        ctx.lineWidth = 20 * s;
        ctx.lineCap = "round";
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x - nx * tailLen, y - ny * tailLen);
        ctx.stroke();

        // “корона”
        ctx.globalCompositeOperation = "lighter";
        const r = 36 * s;
        const g = ctx.createRadialGradient(x, y, 2 * s, x, y, r);
        g.addColorStop(0, "rgba(225,190,255,0.95)");
        g.addColorStop(0.55, "rgba(120,40,180,0.35)");
        g.addColorStop(1, "rgba(80,20,130,0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r, 0, TAU);
        ctx.fill();

        // “зубчастий” імпульс
        ctx.strokeStyle = "rgba(190,130,255,0.25)";
        ctx.lineWidth = 2 * s;
        const rr = (20 + rng() * 8) * s;
        ctx.beginPath();
        ctx.arc(x, y, rr, 0, TAU);
        ctx.stroke();

        ctx.restore();
    };

    const drawRunesRing = (t01: number) => {
        const t = easeOutCubic(t01);
        const rot = t01 * 2.2;

        const ringR = lerp(coreMax * 1.2, maxR * 0.55, t);
        const alpha = lerp(0.0, 0.75, t) * (1 - t01 * 0.25);

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rot);

        // світіння кільця
        ctx.globalCompositeOperation = "lighter";
        ctx.strokeStyle = `rgba(170,90,255,${alpha * 0.55})`;
        ctx.lineWidth = 3 * s;
        ctx.beginPath();
        ctx.arc(0, 0, ringR, 0, TAU);
        ctx.stroke();

        // “руни” (маленькі риски/гліфи по колу)
        const glyphs = 34;
        for (let i = 0; i < glyphs; i++) {
            const a = (i / glyphs) * TAU;
            const gLen = lerp(8, 18, rng()) * s;
            const gW = lerp(1.2, 2.6, rng()) * s;

            const x1 = Math.cos(a) * (ringR - 6 * s);
            const y1 = Math.sin(a) * (ringR - 6 * s);
            const x2 = Math.cos(a) * (ringR - 6 * s + gLen);
            const y2 = Math.sin(a) * (ringR - 6 * s + gLen);

            ctx.strokeStyle = `rgba(210,160,255,${alpha * lerp(0.15, 0.35, rng())})`;
            ctx.lineWidth = gW;
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();

            // інколи додаємо “поперечку”
            if (rng() < 0.28) {
                const b = a + (rng() - 0.5) * 0.7;
                const tx = Math.cos(b) * (ringR - 2 * s);
                const ty = Math.sin(b) * (ringR - 2 * s);
                ctx.lineWidth = gW * 0.9;
                ctx.beginPath();
                ctx.moveTo(tx, ty);
                ctx.lineTo(tx + (rng() - 0.5) * 12 * s, ty + (rng() - 0.5) * 12 * s);
                ctx.stroke();
            }
        }

        ctx.restore();
    };

    const drawCoreVoid = (t01: number) => {
        const t = easeOutCubic(t01);

        // пульсація “діри”
        const pulse = 0.88 + Math.sin(t01 * 18) * 0.06;
        const r = lerp(coreMax * 0.55, coreMax * 1.05, t) * pulse;

        ctx.save();

        // чорне ядро
        ctx.globalCompositeOperation = "source-over";
        const core = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        core.addColorStop(0, "rgba(0,0,0,0.95)");
        core.addColorStop(0.7, "rgba(0,0,0,0.85)");
        core.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = core;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, TAU);
        ctx.fill();

        // фіолетовий край (подвійне світіння)
        ctx.globalCompositeOperation = "lighter";
        const rim = ctx.createRadialGradient(cx, cy, r * 0.25, cx, cy, r * 1.6);
        rim.addColorStop(0, "rgba(160,80,255,0)");
        rim.addColorStop(0.5, `rgba(160,80,255,${lerp(0.15, 0.45, t)})`);
        rim.addColorStop(1, "rgba(160,80,255,0)");
        ctx.fillStyle = rim;
        ctx.beginPath();
        ctx.arc(cx, cy, r * 1.6, 0, TAU);
        ctx.fill();

        ctx.restore();
    };

    const drawShockwaves = (t01: number) => {
        const t = easeOutCubic(t01);

        // 3 хвилі з різними темпами
        const waves = [
            { k: 1.00, w1: 70, w2: 140, a1: 0.80 },
            { k: 1.12, w1: 18, w2: 52, a1: 0.55 },
            { k: 0.92, w1: 10, w2: 36, a1: 0.40 },
        ];

        for (let i = 0; i < waves.length; i++) {
            const ww = waves[i];
            const rt = clamp01(t01 * (1 + i * 0.1));
            const rr = lerp(0, maxR * ww.k, easeOutCubic(rt));

            // чорна товста хвиля
            ctx.save();
            ctx.globalCompositeOperation = "source-over";
            ctx.strokeStyle = `rgba(0,0,0,${lerp(ww.a1, 0.0, t)})`;
            ctx.lineWidth = lerp(ww.w1, ww.w2, t) * s;
            ctx.beginPath();
            ctx.arc(cx, cy, rr, 0, TAU);
            ctx.stroke();
            ctx.restore();

            // світний край
            ctx.save();
            ctx.globalCompositeOperation = "lighter";
            ctx.strokeStyle = `rgba(170,90,255,${lerp(ww.a1 * 0.45, 0.0, t)})`;
            ctx.lineWidth = lerp(ww.w1 * 0.12, ww.w2 * 0.18, t) * s;
            ctx.beginPath();
            ctx.arc(cx, cy, rr, 0, TAU);
            ctx.stroke();
            ctx.restore();
        }
    };

    const drawFog = (t01: number) => {
        const t = easeOutCubic(t01);

        // загальне затемнення + туман від центру
        ctx.save();
        ctx.globalCompositeOperation = "source-over";
        const fog = ctx.createRadialGradient(cx, cy, 0, cx, cy, maxR);
        fog.addColorStop(0, `rgba(0,0,0,${lerp(0.08, 0.55, t)})`);
        fog.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = fog;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();

        // частинки (іскри + дим)
        ctx.save();
        ctx.globalCompositeOperation = "lighter";
        for (const p of particles) {
            const a = clamp01(p.life);

            // іскра
            ctx.fillStyle = `rgba(205,140,255,${0.35 * a})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size, 0, TAU);
            ctx.fill();

            // дим
            ctx.globalCompositeOperation = "source-over";
            ctx.fillStyle = `rgba(0,0,0,${0.20 * a})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 2.2, 0, TAU);
            ctx.fill();

            ctx.globalCompositeOperation = "lighter";
        }
        ctx.restore();
    };

    const drawLightning = (t01: number) => {
        // блискавки найсильніші в першій половині
        const power = 1 - clamp01((t01 - 0.15) / 0.65);

        ctx.save();
        ctx.globalCompositeOperation = "lighter";

        for (const l of lightnings) {
            const a = clamp01(l.life) * power;

            // inner glow
            ctx.strokeStyle = `rgba(210,160,255,${0.35 * a})`;
            ctx.lineWidth = l.width * 1.2;
            ctx.lineJoin = "round";
            ctx.lineCap = "round";
            ctx.beginPath();
            ctx.moveTo(l.points[0].x, l.points[0].y);
            for (let i = 1; i < l.points.length; i++) ctx.lineTo(l.points[i].x, l.points[i].y);
            ctx.stroke();

            // outer darker stroke (контраст)
            ctx.globalCompositeOperation = "source-over";
            ctx.strokeStyle = `rgba(0,0,0,${0.22 * a})`;
            ctx.lineWidth = l.width * 2.4;
            ctx.beginPath();
            ctx.moveTo(l.points[0].x, l.points[0].y);
            for (let i = 1; i < l.points.length; i++) ctx.lineTo(l.points[i].x, l.points[i].y);
            ctx.stroke();

            ctx.globalCompositeOperation = "lighter";
        }

        ctx.restore();
    };

    const drawBurst = (t01: number) => {
        const t = easeOutCubic(t01);

        // керований шейк (менше хаосу ніж Math.random)
        const shakeStrength = (1 - t01) * 5.5 * s;
        const a = rng() * TAU;
        const shx = Math.cos(a) * shakeStrength;
        const shy = Math.sin(a) * shakeStrength;

        ctx.save();
        ctx.translate(shx, shy);

        // background vignette
        const vign = ctx.createRadialGradient(
            cx,
            cy,
            Math.min(W, H) * 0.10,
            cx,
            cy,
            Math.min(W, H) * 0.95
        );
        vign.addColorStop(0, `rgba(0,0,0,${lerp(0.10, 0.45, t)})`);
        vign.addColorStop(1, `rgba(0,0,0,${lerp(0.25, 0.80, t)})`);
        ctx.globalCompositeOperation = "source-over";
        ctx.fillStyle = vign;
        ctx.fillRect(0, 0, W, H);

        // layers
        drawCoreVoid(t01);
        drawShockwaves(t01);
        drawRunesRing(t01);
        drawLightning(t01);
        drawFog(t01);

        ctx.restore();
    };

    return (time: number) => {
        const elapsed = time - startTime;
        if (elapsed < 0) return true;

        // чистимо кожен кадр
        ctx.clearRect(0, 0, W, H);

        // ===== Phase 1: flight =====
        if (elapsed <= FLIGHT_MS) {
            const p = clamp01(elapsed / FLIGHT_MS);
            const e = easeInOutQuad(p);

            const x = lerp(startX, cx, e);
            const y = lerp(startY, cy, e);

            drawBolt(x, y, p);
            return true;
        }

        // ===== Phase 2: burst =====
        const burstElapsed = elapsed - FLIGHT_MS;
        const p2 = clamp01(burstElapsed / BURST_MS);

        if (!spawned) {
            spawnParticles();
            spawnLightning(7); // стартовий набір
            spawned = true;
        }

        // у перші ~300мс додаємо блискавки “пакетами”
        if (p2 < 0.35 && rng() < 0.25) spawnLightning(1);

        updateParticles(1 / 60);
        updateLightning(1 / 60);
        drawBurst(p2);

        return elapsed < TOTAL_MS;
    };
};